import type { Principle, ProcessStep, SearchArea } from '../types/content';

export const searchSteps: ProcessStep[] = [
  {
    title: 'Understand',
    summary: 'Learn about the organization’s objectives, culture, and requirements.',
    detail:
      'We begin with detailed conversations with the people closest to the role, so the brief reflects strategy, context and culture — not only a job description.',
  },
  {
    title: 'Research',
    summary: 'Identify relevant talent pools and potential candidates.',
    detail:
      'We map the organizations, functions and individuals relevant to the brief, looking beyond those actively seeking a move.',
  },
  {
    title: 'Engage',
    summary: 'Conduct professional, confidential candidate outreach.',
    detail:
      'Potential candidates are approached discreetly and given an accurate, considered picture of the opportunity.',
  },
  {
    title: 'Connect',
    summary: 'Facilitate communication and support the search process.',
    detail:
      'We present assessed candidates, coordinate interviews and keep both sides informed through to a decision.',
  },
];

export const principles: Principle[] = [
  {
    title: 'Research-led search',
    description:
      'Every search begins with structured research into the relevant talent market, rather than relying only on individuals already known to us.',
  },
  {
    title: 'Professional and discreet engagement',
    description:
      'We approach candidates with care and confidentiality, representing your organization accurately and respectfully.',
  },
  {
    title: 'Role-specific candidate assessment',
    description:
      'Candidates are assessed against criteria agreed for the specific role and organizational context — not a generic template.',
  },
  {
    title: 'Clear communication throughout',
    description:
      'You receive regular, candid updates on progress, market response and any adjustments the search may need.',
  },
];

export const boardSearchAreas: SearchArea[] = [
  {
    title: 'Non-Executive Directors',
    description:
      'Independent directors who bring external perspective, constructive challenge and relevant sector or functional experience to the board.',
  },
  {
    title: 'Board and committee expertise',
    description:
      'Candidates with experience of audit, risk, remuneration and nomination committees, including potential committee chairs.',
  },
  {
    title: 'C-suite leadership',
    description:
      'Chief executives and senior officers across finance, operations, technology, people and commercial leadership.',
  },
  {
    title: 'Risk, compliance, and governance',
    description:
      'Leaders with depth in regulatory environments, internal control, compliance frameworks and governance practice.',
  },
  {
    title: 'Industry-specific technical leadership',
    description:
      'Senior leaders whose technical or sector expertise is central to the organization’s strategy and operations.',
  },
];

export const values: Principle[] = [
  {
    title: 'Discretion',
    description:
      'Searches often involve sensitive organizational change and individuals in prominent roles. Confidentiality is a default, not an option.',
  },
  {
    title: 'Rigour',
    description:
      'We favour structured research and defined assessment criteria over instinct and familiarity.',
  },
  {
    title: 'Candour',
    description:
      'We give clients and candidates an honest view — of the market, of fit, and of how a search is progressing.',
  },
  {
    title: 'Respect',
    description:
      'Every person we approach is treated as a professional peer, whether or not the opportunity proves right for them.',
  },
];
